import { StandardError } from "@core-ts/StandardError"
import Long from "long"
import { TimeStampNativeType } from "./clients/StorageServiceClient"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import advancedFormat from "dayjs/plugin/advancedFormat"
import customParseFormat from "dayjs/plugin/customParseFormat"
import weekOfYear from "dayjs/plugin/weekOfYear"
import dayjs_plugin_isoWeek from "dayjs/plugin/isoWeek"

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(weekOfYear)
dayjs.extend(dayjs_plugin_isoWeek)

export class TimeStamp {
    //protected timeStamp: TimeStamp | null = null
    private seconds: number // Unix timestamp, always in UTC
    private fraction: number
    private precision: number
    private isDate: boolean

    protected constructor(seconds: number, fraction: number = 0, precision: number = 0, isDate: boolean = false) {
        this.seconds = seconds
        this.fraction = fraction
        this.precision = precision
        this.isDate = isDate
    }

    // TODO: This should not modify the existing object, only return a new one
    public reducePrecisionToDate(): TimeStamp {
        this.seconds = Math.round(this.seconds / 86400) * 86400 // This also solves the issue that some older databases store date timestamps in CET or CEST

        this.fraction = 0
        this.precision = 0
        this.isDate = true

        return this
    }

    protected significantDigits(n: number, digits: number): string {
        return n.toFixed(0).padStart(digits, "0")
    }

    public toUTCStringSeconds(): string {
        if (this.precision !== 0) throw new StandardError("TimeStamp Conversion to string would lose precision")

        return this.toUTCStringSecondsLosePrecision()
    }

    //    private localeTimeStringCache: Map<string, dayjs.Dayjs> = new Map()

    public toLocalTimeString(timeZone: string): string {
        //if(!this.localeTimeStringCache.has(timeZone)) {
        //    this.localeTimeStringCache.set(timeZone, dayjs(this.toUnixTimestamp()*1000).tz(timeZone))

        //    Intl.DateTimeFormat().resolvedOptions().timeZone

        //        return (new Date(this.toUnixTimestamp()*1000)).toLocaleString('sv-SE', { timeZone: timeZone })

        const d = dayjs(this.toUnixTimestamp() * 1000)

        return d.tz(timeZone).format("YYYY-MM-DD HH:mm:ss")
    }

    public toYear(timeZone: string): number {
        const d = dayjs(this.toUnixTimestamp() * 1000)

        // Must be better way?
        return parseInt(d.tz(timeZone).format("YYYY"))
    }

    public toWeek(timeZone: string): number {
        const d = dayjs(this.toUnixTimestamp() * 1000)

        // Must be better way?
        return parseInt(d.tz(timeZone).format("WW"))
    }

    public static fromWeek(year: number, week: number): TimeStamp {
        let d = dayjs().year(year).week(week).startOf("week")

        //        log.debug(`fromWeek: ${year} ${week} ${d.format("YYYY-MM-DD HH:mm:ss")} ${d.day()}`)

        // The day we get here in the dev enviroment is a sunday from the week before so we need to correct it
        // Is this a bug in dayjs? Or is it because weekdays start on sunday or monday?
        if (d.day() === 0 || d.day() === 6) {
            // This code is a bit of magic but it is necessary to get the correct date in both the dev and prod enviroments
            //            log.debug(`fromWeek: add`)
            d = d.add(1, "day")
        } else {
            //            log.debug(`fromWeek: no add`)
        }

        return new TimeStamp(d.unix())
    }

    public toISOString(): string {
        // Could be improved by also returning fraction part
        //        if (this.precision != 0)
        //            throw new StandardError("TimeStamp Conversion to string would lose precision");

        return new Date(this.toUnixTimestamp() * 1000).toISOString()
    }

    public toUTCStringSecondsLosePrecision(): string {
        const d = new Date(this.seconds * 1000)
        let stringDate: string = ""

        stringDate += this.significantDigits(d.getUTCFullYear(), 4) + "-"
        stringDate += this.significantDigits(d.getUTCMonth() + 1, 2) + "-"
        stringDate += this.significantDigits(d.getUTCDate(), 2)

        if (!this.isDate) {
            stringDate += " " + this.significantDigits(d.getUTCHours(), 2) + ":"
            stringDate += this.significantDigits(d.getUTCMinutes(), 2) + ":"
            stringDate += this.significantDigits(d.getUTCSeconds(), 2)
        }

        return stringDate
    }

    public getFraction(): number {
        return this.fraction / 10 ** this.precision
    }

    public toUTCString(): string {
        let stringDate: string = this.toUTCStringSecondsLosePrecision()

        if (this.precision > 0) {
            stringDate += "." + this.significantDigits(this.fraction, this.precision)
        }

        return stringDate
    }

    toUnixTimestamp(): number {
        // TODO: This only get second resolution, it could be improved to at least milliseconds
        return this.seconds
    }

    static fromUnixTimestamp(unixTimeStamp: number): TimeStamp {
        return new TimeStamp(unixTimeStamp)
    }

    static fromDateObjectSecondsOnly(d: Date): TimeStamp {
        return new TimeStamp(d.getTime() / 1000)
    }

    static fromDateObjectDateOnly(d: Date): TimeStamp {
        return new TimeStamp(d.getTime() / 1000, 0, 0, true)
    }

    static fromUTCStringSeconds(utcString: string): TimeStamp {
        // Check that it matches YYYY-MM-DD HH:MM:SS
        if (!utcString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/))
            throw new StandardError("Invalid UTC string format")
        const date = new Date(utcString + "Z")

        return new TimeStamp(date.getTime() / 1000)
    }

    static now(): TimeStamp {
        // TODO: This only get second precision, should we improve that?
        const unixtimeNow = Math.round(new Date().getTime() / 1000)

        return TimeStamp.fromUnixTimestamp(unixtimeNow)
    }

    public toLong(): Long {
        if (this.precision > 9) throw new StandardError("Unsupported precision for GRPC timestamps")

        let low = 0

        if (this.isDate) {
            low = (9 << 27) | 0 // Day precision
        } else if (this.precision === 9) {
            low = 0xc0000000 + this.fraction
        } else {
            low = ((this.precision & 0xf) << 27) + this.fraction
        }

        return new Long(low, this.seconds)
    }

    public isLessThanSecondsOld(seconds: number): boolean {
        return this.seconds + seconds >= new Date().getTime() / 1000
    }

    static fromDataServiceTimeStamp(timestamp: number): TimeStamp | null {
        return TimeStamp.fromUnixTimestamp(timestamp + 1262304000)
    }

    static fromNativeTimeStamp(ts: TimeStampNativeType): TimeStamp | null {
        if (ts === null) return null
        if (typeof ts === "number") return new TimeStamp(ts)
        if (Long.isLong(ts)) return TimeStamp.fromLong(ts)

        throw new StandardError("Failed to convert to Variant from Native TimeStamp")
    }

    toNativeTimeStamp(): TimeStampNativeType {
        if (this.precision === 0) return this.seconds

        return this.toLong()
    }

    toDataServiceTimeStampLosePrecision(): number {
        return this.seconds - 1262304000
    }

    toDataServiceTimeStringLosePrecision(): string {
        const d = new Date(this.seconds * 1000)
        let stringDate: string = ""

        stringDate += this.significantDigits(d.getUTCFullYear(), 4) + "-"
        stringDate += this.significantDigits(d.getUTCMonth() + 1, 2) + "-"
        stringDate += this.significantDigits(d.getUTCDate(), 2)

        stringDate += " " + this.significantDigits(d.getUTCHours(), 2) + ":"
        stringDate += this.significantDigits(d.getUTCMinutes(), 2) + ":"
        stringDate += this.significantDigits(d.getUTCSeconds(), 2)

        return stringDate
    }

    toDateObjectSecondsOnly(): Date {
        return new Date(this.seconds * 1000)
    }

    static fromLongUnixTimestamp(tsLong: Long): TimeStamp {
        return new TimeStamp(tsLong.low)
    }

    static fromLong(tsLong: Long): TimeStamp {
        const seconds = tsLong.high
        let fraction: number
        let precision: number
        let isDate: boolean = false

        if (tsLong.low & 0x80000000) {
            if (!(tsLong.low & 0x40000000)) throw new StandardError("Unsupported timestamp format")
            // Nanosecond precision
            precision = 9
            const lowBits = tsLong.low & 0x3fffffff
            if (lowBits >= 1000000000) throw new StandardError("Invalid timestamp, low bits are out of bounds (ns)")
            fraction = lowBits / 1000000000
        } else {
            // Other precisions
            const precisionBits = (tsLong.low >> 27) & 0x1f
            const lowBits = tsLong.low & 0x7ffffff

            if (precisionBits >= 10) throw new StandardError("Invalid timestamp, precision >= 10 is not supported")
            if (precisionBits === 9) {
                if (lowBits === 0) {
                    // Days
                    precision = 0
                    isDate = true
                } else {
                    throw new StandardError("Unsupported subformat under precision 9")
                }
            } else {
                precision = precisionBits
                const divider = 10 ** precision
                if (lowBits >= divider) throw new StandardError("Invalid timestamp, low bits are out of bounds")
            }

            fraction = lowBits
        }

        return new TimeStamp(seconds, fraction, precision, isDate)
    }

    /**
     * Return a new Timestamp time added.
     * OBS!!! Does not account for leap time.
     * @param value Integer value of added time
     * @param unit Time unit of the value
     */
    public plus(value: number, unit: TimeUnit): TimeStamp {
        // TODO: Implement leap time compensation
        switch (unit) {
            case TimeUnit.Second: {
                return new TimeStamp(this.seconds + value, this.fraction, this.precision, this.isDate)
            }

            case TimeUnit.Minute: {
                return new TimeStamp(this.seconds + value * 60, this.fraction, this.precision, this.isDate)
            }

            case TimeUnit.Hour: {
                return new TimeStamp(this.seconds + value * 3600, this.fraction, this.precision, this.isDate)
            }

            case TimeUnit.Day: {
                return new TimeStamp(this.seconds + value * 86400, this.fraction, this.precision, this.isDate)
            }

            case TimeUnit.Week: {
                return new TimeStamp(this.seconds + value * 86400 * 7, this.fraction, this.precision, this.isDate)
            }

            default: {
                throw new StandardError(`Unknown TimeUnit: ${unit}`)
            }
        }
    }

    /**
     * Return a new Timestamp time removed.
     * OBS!!! Does not account for leap time.
     * @param value Integer value of removed time
     * @param unit Time unit of the value
     */
    public minus(value: number, unit: TimeUnit): TimeStamp {
        // TODO: Implement leap time compensation
        switch (unit) {
            case TimeUnit.Second: {
                return new TimeStamp(this.seconds - value, this.fraction, this.precision, this.isDate)
            }

            case TimeUnit.Minute: {
                return new TimeStamp(this.seconds - value * 60, this.fraction, this.precision, this.isDate)
            }

            case TimeUnit.Hour: {
                return new TimeStamp(this.seconds - value * 3600, this.fraction, this.precision, this.isDate)
            }

            case TimeUnit.Day: {
                return new TimeStamp(this.seconds - value * 86400, this.fraction, this.precision, this.isDate)
            }

            case TimeUnit.Week: {
                return new TimeStamp(this.seconds - value * 86400 * 7, this.fraction, this.precision, this.isDate)
            }

            default: {
                throw new StandardError(`Unknown TimeUnit: ${unit}`)
            }
        }
    }

    /**
     * OBS!!! Might not interact with precision correctly
     * @returns True if the timestamp is in the past
     */
    public hasPassed(): boolean {
        return this.seconds < new Date().getTime() / 1000
    }

    public getLocalHour(timeZone: string): number {
        return parseInt(this.toLocalTimeString(timeZone).split(" ")[1].split(":")[0])
    }

    public getLocalDay(timeZone: string): number {
        return parseInt(this.toLocalTimeString(timeZone).split(" ")[0].split("-")[2])
    }
    
    valueOf(): number {
        return this.seconds
    }
}

export enum TimeUnit {
    Second = 0,
    Minute = 1,
    Hour = 2,
    Day = 3,
    Week = 4,
}
